import React, { useState, useEffect } from 'react';
import {
    Box, Typography, Card, CardContent, Grid, Button, Chip, Avatar,
    Dialog, DialogTitle, DialogContent, DialogActions, TextField,
    Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
    Pagination, FormControl, InputLabel, Select, MenuItem, 
    Alert, CircularProgress, IconButton, Tooltip, Paper,
    ImageList, ImageListItem, Skeleton, Checkbox, Toolbar,
    ListItemText, Menu, Divider, Stack, Badge, Tabs, Tab
} from '@mui/material';
import {
    CheckCircle as ValidateIcon,
    Cancel as RejectIcon,
    Visibility as ViewIcon,
    History as HistoryIcon,
    FilterList as FilterIcon,
    Refresh as RefreshIcon,
    PhotoLibrary as PhotoIcon,
    SelectAll as SelectAllIcon,
    ClearAll as ClearAllIcon,
    Delete as DeleteIcon,
    MoreVert as MoreVertIcon,
    BatchPrediction as BatchIcon,
    Translate as TranslateIcon
} from '@mui/icons-material';
import { useNotifications } from '../../context/NotificationContext';
import api from '../../services/api';
import listingValidationService from '../../services/listingValidationService';
import adminProductService from '../../services/adminProductService';
import ProductValidation from './ProductValidation';

/**
 * Composant de validation des annonces pour les administrateurs
 * @returns {React.ReactNode} Interface de validation des listings
 */
const ListingValidation = () => {
    // États principaux
    const [activeTab, setActiveTab] = useState(0); // 0: Annonces, 1: Produits
    const [pendingListings, setPendingListings] = useState([]);
    const [loading, setLoading] = useState(true);
    const [totalPages, setTotalPages] = useState(1);
    const [currentPage, setCurrentPage] = useState(1);
    
    // Comptages pour badges
    const [pendingListingsCount, setPendingListingsCount] = useState(0);
    const [pendingProductsCount, setPendingProductsCount] = useState(0);
    const [loadingCounts, setLoadingCounts] = useState(true);
    
    // États modales
    const [validateDialog, setValidateDialog] = useState({ open: false, listing: null });
    const [rejectDialog, setRejectDialog] = useState({ open: false, listing: null });
    const [detailDialog, setDetailDialog] = useState({ open: false, listing: null });
    
    // États formulaires
    const [rejectReason, setRejectReason] = useState('');
    const [validationNotes, setValidationNotes] = useState('');
    const [submitting, setSubmitting] = useState(false);
    
    // États pour la traduction
    const [translatedReason, setTranslatedReason] = useState('');
    const [translating, setTranslating] = useState(false);
    const [translationResult, setTranslationResult] = useState(null);
    const [showTranslation, setShowTranslation] = useState(false);
    
    // États pour la sélection multiple et actions en lot
    const [selectedListings, setSelectedListings] = useState([]);
    const [batchActionDialog, setBatchActionDialog] = useState({ open: false, action: null });
    const [batchRejectReason, setBatchRejectReason] = useState('');
    const [batchActionMenu, setBatchActionMenu] = useState(null);
    const [batchSubmitting, setBatchSubmitting] = useState(false);

    // Filtres
    const [filters, setFilters] = useState({
        category: '',
        city: ''
    });
    
    // Catégories pour le filtre
    const [categories, setCategories] = useState([]);
    
    const { showNotification } = useNotifications();
    
    /**
     * Charger les annonces en attente de validation
     * @param {number} page - Page à charger
     */
    const loadPendingListings = async (page = 1) => {
        setLoading(true);
        try {
            console.log('[ListingValidation] Loading pending listings, page:', page, 'filters:', filters);
            const response = await listingValidationService.getPendingListings({
                page,
                limit: 10,
                ...Object.fromEntries(Object.entries(filters).filter(([_, v]) => v))
            });
            
            if (response.success) {
                setPendingListings(response.listings);
                setTotalPages(response.pagination.pages);
                setCurrentPage(page);
                console.log('[ListingValidation] Loaded', response.listings.length, 'pending listings');
            } else {
                showNotification('Erreur lors du chargement des annonces', 'error');
            }
        } catch (error) {
            console.error('[ListingValidation] Error loading pending listings:', error);
            if (error.response?.status === 403) {
                showNotification('Vous n\'avez pas les permissions pour voir les annonces en attente', 'error');
            } else {
                showNotification('Erreur lors du chargement des annonces', 'error');
            }
        } finally {
            setLoading(false);
        }
    };
    
    /**
     * Charger les catégories pour le filtre
     */
    const loadCategories = async () => {
        try {
            const response = await api.get('/categories');
            if (response.data.success) {
                setCategories(response.data.categories);
            }
        } catch (error) {
            console.error('[ListingValidation] Error loading categories:', error);
        }
    };
    
    /**
     * Charger les comptages des validations en attente
     */
    const loadValidationCounts = async () => {
        try {
            setLoadingCounts(true);
            // Comptage des annonces en attente
            const listingsRes = await listingValidationService.getPendingListings({ page: 1, limit: 1 });
            if (listingsRes.success) {
                setPendingListingsCount(listingsRes.pagination?.total || 0);
            }
            
            // Comptage des produits en attente
            const productsRes = await adminProductService.getPendingProducts({ page: 1, limit: 1, status: 'pending' });
            if (productsRes.products) {
                setPendingProductsCount(productsRes.pagination?.total || productsRes.products.length || 0);
            }
        } catch (error) {
            console.error('[ListingValidation] Error loading validation counts:', error);
        } finally {
            setLoadingCounts(false);
        }
    };
    
    // Charger les données au montage et quand les filtres changent
    useEffect(() => {
        loadPendingListings();
    }, [filters]);
    
    useEffect(() => {
        loadCategories();
        loadValidationCounts();
    }, []);
    
    /**
     * Valider une annonce
     */
    const handleValidate = async () => {
        if (!validateDialog.listing) return;
        
        setSubmitting(true);
        try {
            console.log('[ListingValidation] Validating listing:', validateDialog.listing.id);
            const response = await listingValidationService.validateListing(
                validateDialog.listing.id,
                { notes: validationNotes || undefined }
            );
            
            if (response.success) {
                showNotification('Annonce validée avec succès', 'success');
                setValidateDialog({ open: false, listing: null });
                setValidationNotes('');
                loadPendingListings(currentPage);
            } else {
                showNotification(response.message || 'Erreur lors de la validation', 'error');
            }
        } catch (error) {
            console.error('[ListingValidation] Error validating listing:', error);
            if (error.response?.status === 403) {
                showNotification('Vous n\'avez pas les permissions pour valider cette annonce', 'error');
            } else if (error.response?.status === 404) {
                showNotification('Annonce non trouvée', 'error');
            } else if (error.response?.status === 400) {
                showNotification(error.response.data.message || 'Cette annonce ne peut pas être validée', 'error');
            } else {
                showNotification('Erreur lors de la validation', 'error');
            }
        } finally {
            setSubmitting(false);
        }
    };
    
    /**
     * Rejeter une annonce
     */
    const handleReject = async () => {
        if (!rejectDialog.listing || !rejectReason.trim()) {
            showNotification('Le motif de rejet est obligatoire', 'warning');
            return;
        }
        
        if (rejectReason.trim().length < 10) {
            showNotification('Le motif doit contenir au moins 10 caractères', 'warning');
            return;
        }
        
        setSubmitting(true);
        try {
            console.log('[ListingValidation] Rejecting listing:', rejectDialog.listing.id);
            const response = await listingValidationService.rejectListing(
                rejectDialog.listing.id,
                { 
                    reason: rejectReason.trim(),
                    notes: validationNotes || undefined
                }
            );
            
            if (response.success) {
                showNotification('Annonce rejetée avec succès', 'success');
                setRejectDialog({ open: false, listing: null });
                setRejectReason('');
                setValidationNotes('');
                loadPendingListings(currentPage);
            } else {
                showNotification(response.message || 'Erreur lors du rejet', 'error');
            }
        } catch (error) {
            console.error('[ListingValidation] Error rejecting listing:', error);
            if (error.response?.status === 403) {
                showNotification('Vous n\'avez pas les permissions pour rejeter cette annonce', 'error');
            } else if (error.response?.status === 404) {
                showNotification('Annonce non trouvée', 'error');
            } else if (error.response?.status === 400) {
                showNotification(error.response.data.message || 'Cette annonce ne peut pas être rejetée', 'error');
            } else {
                showNotification('Erreur lors du rejet', 'error');
            }
        } finally {
            setSubmitting(false);
        }
    };

    /**
     * Traduire le motif de rejet
     */
    const handleTranslateReason = async () => {
        if (!rejectDialog.listing || !rejectReason.trim()) {
            showNotification('Veuillez saisir un motif de rejet avant de traduire', 'warning');
            return;
        }

        if (rejectReason.trim().length < 10) {
            showNotification('Le motif doit contenir au moins 10 caractères', 'warning');
            return;
        }

        setTranslating(true);
        try {
            console.log('[ListingValidation] Translating rejection reason for listing:', rejectDialog.listing.id);
            const response = await api.post(`/admin/listings/${rejectDialog.listing.id}/translate-rejection`, {
                reason: rejectReason.trim()
            });

            if (response.data.success) {
                const translation = response.data.translation;
                setTranslationResult(translation);

                if (translation.needsTranslation) {
                    if (translation.translatedReason) {
                        setTranslatedReason(translation.translatedReason);
                        setShowTranslation(true);
                        showNotification(`Traduction générée en ${translation.targetLanguage}`, 'success');
                    } else {
                        showNotification(response.data.message || 'Erreur lors de la traduction', 'error');
                    }
                } else {
                    showNotification(response.data.message, 'info');
                }
            } else {
                showNotification(response.data.message || 'Erreur lors de la traduction', 'error');
            }
        } catch (error) {
            console.error('[ListingValidation] Error translating rejection reason:', error);
            if (error.response?.status === 503) {
                showNotification('Service de traduction indisponible', 'error');
            } else if (error.response?.status === 456) {
                showNotification('Quota de traduction épuisé. Veuillez traduire manuellement.', 'warning');
            } else {
                showNotification('Erreur lors de la traduction', 'error');
            }
        } finally {
            setTranslating(false);
        }
    };

    /**
     * Confirmer le rejet avec traduction
     */
    const handleConfirmRejectionWithTranslation = async () => {
        if (!rejectDialog.listing) return;

        const reasonToUse = showTranslation && translatedReason ? translatedReason : rejectReason.trim();

        setSubmitting(true);
        try {
            console.log('[ListingValidation] Rejecting listing with translation:', rejectDialog.listing.id);
            const response = await listingValidationService.rejectListing(
                rejectDialog.listing.id,
                { 
                    reason: reasonToUse,
                    notes: validationNotes || undefined
                }
            );
            
            if (response.success) {
                showNotification('Annonce rejetée avec succès', 'success');
                setRejectDialog({ open: false, listing: null });
                setRejectReason('');
                setTranslatedReason('');
                setTranslationResult(null);
                setShowTranslation(false);
                setValidationNotes('');
                loadPendingListings(currentPage);
            } else {
                showNotification(response.message || 'Erreur lors du rejet', 'error');
            }
        } catch (error) {
            console.error('[ListingValidation] Error rejecting listing:', error);
            showNotification('Erreur lors du rejet', 'error');
        } finally {
            setSubmitting(false);
        }
    };
    
    /**
     * Sélectionner/Désélectionner toutes les annonces
     */
    const handleSelectAll = (event) => {
        if (event.target.checked) {
            const allListingIds = pendingListings.map(listing => listing.id);
            setSelectedListings(allListingIds);
        } else {
            setSelectedListings([]);
        }
    };

    /**
     * Sélectionner/Désélectionner une annonce
     */
    const handleSelectListing = (listingId) => {
        setSelectedListings(prev => {
            if (prev.includes(listingId)) {
                return prev.filter(id => id !== listingId);
            } else {
                return [...prev, listingId];
            }
        });
    };

    /**
     * Ouvrir le menu des actions en lot
     */
    const handleBatchMenuOpen = (event) => {
        setBatchActionMenu(event.currentTarget);
    };

    /**
     * Fermer le menu des actions en lot
     */
    const handleBatchMenuClose = () => {
        setBatchActionMenu(null);
    };

    /**
     * Appliquer une action en lot sur les annonces sélectionnées
     */
    const handleBatchAction = async () => {
        if (batchActionDialog.action === 'validate') {
            // Valider en lot
            setBatchSubmitting(true);
            try {
                const responses = await Promise.all(
                    selectedListings.map((listingId) =>
                        listingValidationService.validateListing(listingId, { notes: validationNotes || undefined })
                    )
                );

                const success = responses.filter(res => res.success);
                const failed = responses.filter(res => !res.success);

                if (success.length > 0) {
                    showNotification(`${success.length} annonces validées avec succès`, 'success');
                }
                if (failed.length > 0) {
                    showNotification(`${failed.length} erreurs lors de la validation en lot`, 'error');
                }

                setBatchActionDialog({ open: false, action: null });
                setValidationNotes('');
                setSelectedListings([]);
                loadPendingListings(currentPage);
            } catch (error) {
                console.error('[ListingValidation] Error validating listings in batch:', error);
                showNotification('Erreur lors de la validation en lot', 'error');
            } finally {
                setBatchSubmitting(false);
            }
        } else if (batchActionDialog.action === 'reject') {
            // Rejeter en lot
            if (!batchRejectReason.trim()) {
                showNotification('Le motif de rejet est obligatoire pour l\'action en lot', 'warning');
                return;
            }

            if (batchRejectReason.trim().length < 10) {
                showNotification('Le motif doit contenir au moins 10 caractères', 'warning');
                return;
            }

            setBatchSubmitting(true);
            try {
                const responses = await Promise.all(
                    selectedListings.map((listingId) =>
                        listingValidationService.rejectListing(listingId, { reason: batchRejectReason.trim(), notes: validationNotes || undefined })
                    )
                );

                const success = responses.filter(res => res.success);
                const failed = responses.filter(res => !res.success);

                if (success.length > 0) {
                    showNotification(`${success.length} annonces rejetées avec succès`, 'success');
                }
                if (failed.length > 0) {
                    showNotification(`${failed.length} erreurs lors du rejet en lot`, 'error');
                }

                setBatchActionDialog({ open: false, action: null });
                setBatchRejectReason('');
                setValidationNotes('');
                setSelectedListings([]);
                loadPendingListings(currentPage);
            } catch (error) {
                console.error('[ListingValidation] Error rejecting listings in batch:', error);
                showNotification('Erreur lors du rejet en lot', 'error');
            } finally {
                setBatchSubmitting(false);
            }
        }
    };

    /**
     * Formater la date pour affichage
     * @param {string} dateString - Date à formater
     * @returns {string} Date formatée
     */
    const formatDate = (dateString) => {
        return new Date(dateString).toLocaleDateString('fr-FR', {
            year: 'numeric',
            month: '2-digit',
            day: '2-digit',
            hour: '2-digit',
            minute: '2-digit'
        });
    };
    
    /**
     * Obtenir le nom de la catégorie
     * @param {Object} listing - Listing
     * @returns {string} Nom de la catégorie
     */
    const getCategoryName = (listing) => {
        return listing.category?.name || listing.Category?.name || 'Non définie';
    };
    
    /**
     * Obtenir l'URL de l'image principale
     * @param {Object} listing - Listing
     * @returns {string|null} URL de l'image
     */
    const getMainImageUrl = (listing) => {
        const images = listing.images || listing.Images || [];
        return images.length > 0 ? images[0].image_url : null;
    };
    
    /**
     * Détecter la langue préférée du propriétaire
     * @param {Object} listing - Listing avec son propriétaire
     * @returns {string} Code de langue (fr, en, ar, etc.)
     */
    const detectUserLanguage = (listing) => {
        if (!listing || !listing.owner) return 'en';
        
        const owner = listing.owner;
        // Vérification des préférences utilisateur selon la structure réelle du modèle
        if (owner.preferences && typeof owner.preferences === 'object') {
            const preferences = typeof owner.preferences === 'string' 
                ? JSON.parse(owner.preferences) 
                : owner.preferences;
            
            if (preferences.language) {
                return preferences.language;
            }
        }
        
        // Langue par défaut
        return 'en';
    };
    
    /**
     * Motifs de rejet prédéfinis
     */
    const predefinedReasons = [
        'Photos de mauvaise qualité ou non conformes',
        'Description incomplète ou incorrecte',
        'Prix non réaliste ou non conforme au marché',
        'Informations de contact manquantes ou incorrectes',
        'Contenu inapproprié ou non conforme aux conditions d\'utilisation',
        'Localisation incorrecte ou manquante'
    ];
    
    // Affichage du loading
    if (loading && pendingListings.length === 0) {
        return (
            <Box p={3}>
                <Typography variant="h4" component="h1" gutterBottom>
                    Validation des Annonces
                </Typography>
                <Box display="flex" justifyContent="center" p={4}>
                    <CircularProgress />
                </Box>
            </Box>
        );
    }
    
    return (
        <Box p={3}>
            {/* En-tête */}
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
                <Typography variant="h4" component="h1">
                    Validation des Contenus
                </Typography>
                <Box display="flex" alignItems="center" gap={2}>
                    <Chip
                        label={`${pendingListingsCount} Listings en attente / ${pendingProductsCount} produits en attente`}
                        sx={{
                            bgcolor: '#ff9800',
                            color: '#ffffff',
                            fontWeight: 700,
                            borderColor: 'transparent'
                        }}
                    />
                    <IconButton 
                        onClick={() => { loadPendingListings(currentPage); loadValidationCounts(); }}
                        disabled={loading || loadingCounts}
                        color="primary"
                    >
                        <RefreshIcon />
                    </IconButton>
                </Box>
            </Box>

            {/* Onglets */}
            <Paper sx={{ mb: 3, borderRadius: 2, background: 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)' }}>
              <Tabs
                value={activeTab}
                onChange={(_, newValue) => setActiveTab(newValue)}
                sx={{
                  '& .MuiTab-root': { fontSize: '1rem', fontWeight: 600, textTransform: 'none' },
                  '& .MuiTabs-indicator': { background: 'linear-gradient(45deg, #6200ee 30%, #03dac4 90%)', height: 3 }
                }}
              >
                <Tab 
                  label={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      Validation des Listings
                      {pendingListingsCount > 0 && (
                        <Badge 
                          badgeContent={pendingListingsCount} 
                          color="error"
                          sx={{ '& .MuiBadge-badge': { bgcolor: '#ff5252', fontWeight: 700 } }}
                        />
                      )}
                    </Box>
                  } 
                />
                <Tab 
                  label={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      Validation des Produits
                      {pendingProductsCount > 0 && (
                        <Badge 
                          badgeContent={pendingProductsCount} 
                          color="error"
                          sx={{ '& .MuiBadge-badge': { bgcolor: '#ff5252', fontWeight: 700 } }}
                        />
                      )}
                    </Box>
                  } 
                />
              </Tabs>
            </Paper>

            {/* Contenu onglet Annonces */}
            {activeTab === 0 && (
              <Box>
                {/* Filtres */}
            <Paper elevation={1} sx={{ p: 2, mb: 3 }}>
                <Grid container spacing={2} alignItems="center">
                    <Grid item xs={12} md={4}>
                        <TextField
                            fullWidth
                            label="Filtrer par ville"
                            value={filters.city}
                            onChange={(e) => setFilters(prev => ({ ...prev, city: e.target.value }))}
                            size="small"
                            placeholder="Tapez le nom d'une ville..."
                        />
                    </Grid>
                    <Grid item xs={12} md={4}>
                        <FormControl fullWidth size="small">
                            <InputLabel>Catégorie</InputLabel>
                            <Select
                                value={filters.category}
                                onChange={(e) => setFilters(prev => ({ ...prev, category: e.target.value }))}
                                label="Catégorie"
                            >
                                <MenuItem value="">Toutes</MenuItem>
                                {categories.map((category) => (
                                    <MenuItem key={category.id} value={category.id}>
                                        {category.name}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Grid>
                    <Grid item xs={12} md={4}>
                        <Button
                            variant="outlined"
                            startIcon={<FilterIcon />}
                            onClick={() => loadPendingListings(1)}
                            disabled={loading}
                        >
                            Actualiser
                        </Button>
                    </Grid>
                </Grid>
            </Paper>
            
            {/* Liste des annonces */}
            {pendingListings.length === 0 ? (
                <Alert severity="info" sx={{ mt: 3 }}>
                    {loading ? 'Chargement des annonces...' : 'Aucune annonce en attente de validation.'}
                </Alert>
            ) : (
                <>
                    <TableContainer component={Paper}>
                        {/* Barre d'outils pour actions en lot */}
                        {selectedListings.length > 0 && (
                            <Toolbar
                                sx={{
                                    backgroundColor: 'primary.main',
                                    color: 'primary.contrastText',
                                    borderRadius: '4px 4px 0 0'
                                }}
                            >
                                <Typography variant="h6" sx={{ flex: '1 1 100%' }}>
                                    {selectedListings.length} annonce(s) sélectionnée(s)
                                </Typography>
                                <Tooltip title="Valider la sélection">
                                    <IconButton
                                        color="inherit"
                                        onClick={() => setBatchActionDialog({ open: true, action: 'validate' })}
                                        disabled={batchSubmitting}
                                    >
                                        <ValidateIcon />
                                    </IconButton>
                                </Tooltip>
                                <Tooltip title="Rejeter la sélection">
                                    <IconButton
                                        color="inherit"
                                        onClick={() => setBatchActionDialog({ open: true, action: 'reject' })}
                                        disabled={batchSubmitting}
                                    >
                                        <RejectIcon />
                                    </IconButton>
                                </Tooltip>
                                <Tooltip title="Désélectionner tout">
                                    <IconButton
                                        color="inherit"
                                        onClick={() => setSelectedListings([])}
                                        disabled={batchSubmitting}
                                    >
                                        <ClearAllIcon />
                                    </IconButton>
                                </Tooltip>
                            </Toolbar>
                        )}

                        <Table>
                            <TableHead>
                                <TableRow>
                                    <TableCell padding="checkbox">
                                        <Checkbox
                                            color="primary"
                                            indeterminate={selectedListings.length > 0 && selectedListings.length < pendingListings.length}
                                            checked={pendingListings.length > 0 && selectedListings.length === pendingListings.length}
                                            onChange={handleSelectAll}
                                            disabled={loading}
                                        />
                                    </TableCell>
                                    <TableCell>Annonce</TableCell>
                                    <TableCell>Propriétaire</TableCell>
                                    <TableCell>Catégorie</TableCell>
                                    <TableCell>Ville</TableCell>
                                    <TableCell>Créée le</TableCell>
                                    <TableCell align="center">Actions</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {pendingListings.map((listing) => (
                                    <TableRow
                                        key={listing.id}
                                        hover
                                        selected={selectedListings.includes(listing.id)}
                                        sx={{
                                            '&.Mui-selected': {
                                                backgroundColor: 'action.selected',
                                            }
                                        }}
                                    >
                                        <TableCell padding="checkbox">
                                            <Checkbox
                                                color="primary"
                                                checked={selectedListings.includes(listing.id)}
                                                onChange={() => handleSelectListing(listing.id)}
                                                disabled={loading || batchSubmitting}
                                            />
                                        </TableCell>
                                        <TableCell>
                                            <Box display="flex" alignItems="center">
                                                <Avatar
                                                    src={getMainImageUrl(listing)}
                                                    sx={{ mr: 2, width: 50, height: 50 }}
                                                    variant="rounded"
                                                >
                                                    <PhotoIcon />
                                                </Avatar>
                                                <Box>
                                                    <Typography variant="subtitle2" noWrap sx={{ maxWidth: 200 }}>
                                                        {listing.title}
                                                    </Typography>
                                                    {listing.price && (
                                                        <Typography variant="caption" color="primary" sx={{ fontWeight: 'bold' }}>
                                                            {listing.price}€
                                                        </Typography>
                                                    )}
                                                </Box>
                                            </Box>
                                        </TableCell>
                                        <TableCell>
                                            <Box>
                                                <Box display="flex" alignItems="center">
                                                    <Typography variant="body2">
                                                        {listing.owner?.name || listing.User?.name || 'Anonyme'}
                                                    </Typography>
                                                    <Chip 
                                                        label={detectUserLanguage(listing)}
                                                        size="small"
                                                        variant="outlined"
                                                        sx={{ ml: 1, height: 16, fontSize: '0.6rem', '& .MuiChip-label': { px: 0.8 } }}
                                                    />
                                                </Box>
                                                <Typography variant="caption" color="textSecondary">
                                                    {listing.owner?.email || listing.User?.email}
                                                </Typography>
                                            </Box>
                                        </TableCell>
                                        <TableCell>
                                            <Chip 
                                                label={getCategoryName(listing)}
                                                size="small"
                                                variant="outlined"
                                                color="primary"
                                            />
                                        </TableCell>
                                        <TableCell>
                                            <Typography variant="body2">
                                                {listing.city || 'Non spécifiée'}
                                            </Typography>
                                        </TableCell>
                                        <TableCell>
                                            <Typography variant="body2">
                                                {formatDate(listing.createdAt)}
                                            </Typography>
                                        </TableCell>
                                        <TableCell align="center">
                                            <Box display="flex" gap={1} justifyContent="center">
                                                <Tooltip title="Voir détails">
                                                    <IconButton
                                                        size="small"
                                                        onClick={() => setDetailDialog({ open: true, listing })}
                                                        color="info"
                                                    >
                                                        <ViewIcon />
                                                    </IconButton>
                                                </Tooltip>
                                                <Tooltip title="Valider">
                                                    <IconButton
                                                        size="small"
                                                        onClick={() => setValidateDialog({ open: true, listing })}
                                                        color="success"
                                                    >
                                                        <ValidateIcon />
                                                    </IconButton>
                                                </Tooltip>
                                                <Tooltip title="Rejeter">
                                                    <IconButton
                                                        size="small"
                                                        onClick={() => setRejectDialog({ open: true, listing })}
                                                        color="error"
                                                    >
                                                        <RejectIcon />
                                                    </IconButton>
                                                </Tooltip>
                                            </Box>
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                    
                    {/* Pagination */}
                    {totalPages > 1 && (
                        <Box display="flex" justifyContent="center" mt={3}>
                            <Pagination
                                count={totalPages}
                                page={currentPage}
                                onChange={(event, page) => loadPendingListings(page)}
                                color="primary"
                                disabled={loading}
                            />
                        </Box>
                    )}
                </>
            )}
            
            {/* Dialog Validation */}
            <Dialog
                open={validateDialog.open} 
                onClose={() => !submitting && setValidateDialog({ open: false, listing: null })}
                maxWidth="sm" 
                fullWidth
            >
                <DialogTitle>Valider l'annonce</DialogTitle>
                <DialogContent>
                    <Typography variant="body1" gutterBottom>
                        Êtes-vous sûr de vouloir valider l'annonce <strong>"{validateDialog.listing?.title}"</strong> ?
                    </Typography>
                    <Typography variant="body2" color="textSecondary" gutterBottom>
                        Cette action rendra l'annonce visible publiquement et enverra une notification au propriétaire.
                    </Typography>
                    <TextField
                        fullWidth
                        multiline
                        rows={3}
                        label="Notes internes (optionnel)"
                        value={validationNotes}
                        onChange={(e) => setValidationNotes(e.target.value)}
                        margin="normal"
                        placeholder="Ajouter des notes pour l'équipe (non visibles par l'utilisateur)..."
                        disabled={submitting}
                    />
                </DialogContent>
                <DialogActions>
                    <Button 
                        onClick={() => setValidateDialog({ open: false, listing: null })}
                        disabled={submitting}
                    >
                        Annuler
                    </Button>
                    <Button
                        onClick={handleValidate}
                        color="success"
                        variant="contained"
                        disabled={submitting}
                        startIcon={submitting ? <CircularProgress size={20} /> : <ValidateIcon />}
                    >
                        {submitting ? 'Validation...' : 'Valider'}
                    </Button>
                </DialogActions>
            </Dialog>
            
            {/* Dialog Rejet */}
            <Dialog 
                open={rejectDialog.open} 
                onClose={() => !submitting && setRejectDialog({ open: false, listing: null })}
                maxWidth="sm" 
                fullWidth
            >
                <DialogTitle>Rejeter l'annonce</DialogTitle>
                <DialogContent>
                    <Typography variant="body1" gutterBottom>
                        Pourquoi rejeter l'annonce <strong>"{rejectDialog.listing?.title}"</strong> ?
                    </Typography>
                    <Box display="flex" alignItems="center" mb={1}>
                        <Typography variant="body2" color="textSecondary">
                            Le motif sera envoyé au propriétaire pour qu'il puisse corriger son annonce.
                        </Typography>
                    </Box>
                    <Box display="flex" alignItems="center" mb={2} sx={{ backgroundColor: '#f8f8f8', p: 1, borderRadius: 1 }}>
                        <Chip 
                            label={`Langue du propriétaire: ${detectUserLanguage(rejectDialog.listing)}`}
                            size="small"
                            color="primary"
                            sx={{ mr: 1 }}
                        />
                        <Typography variant="caption" color="textSecondary">
                            Veuillez rédiger le motif de rejet en tenant compte de la langue préférée du propriétaire.
                        </Typography>
                    </Box>
                    
                    {/* Motifs prédéfinis */}
                    <Typography variant="subtitle2" sx={{ mt: 2, mb: 1 }}>
                        Motifs courants :
                    </Typography>
                    <Box display="flex" flexWrap="wrap" gap={1} mb={2}>
                        {predefinedReasons.map((reason, index) => (
                            <Chip
                                key={index}
                                label={reason}
                                variant="outlined"
                                size="small"
                                clickable
                                onClick={() => setRejectReason(reason)}
                                color={rejectReason === reason ? 'primary' : 'default'}
                            />
                        ))}
                    </Box>
                    
                    <TextField
                        fullWidth
                        multiline
                        rows={4}
                        label="Motif du rejet *"
                        value={rejectReason}
                        onChange={(e) => {
                            setRejectReason(e.target.value);
                            // Reset translation when reason changes
                            if (showTranslation) {
                                setShowTranslation(false);
                                setTranslatedReason('');
                                setTranslationResult(null);
                            }
                        }}
                        margin="normal"
                        required
                        error={rejectReason.trim().length > 0 && rejectReason.trim().length < 10}
                        helperText={
                            rejectReason.trim().length > 0 && rejectReason.trim().length < 10
                                ? 'Le motif doit contenir au moins 10 caractères'
                                : 'Ce motif sera envoyé au propriétaire de l\'annonce'
                        }
                        placeholder="Expliquez précisément pourquoi cette annonce ne peut pas être validée..."
                        disabled={submitting || translating}
                    />

                    {/* Bouton de traduction */}
                    <Box display="flex" alignItems="center" gap={2} mt={1} mb={2}>
                        <Button
                            variant="outlined"
                            startIcon={translating ? <CircularProgress size={16} /> : <TranslateIcon />}
                            onClick={handleTranslateReason}
                            disabled={!rejectReason.trim() || rejectReason.trim().length < 10 || submitting || translating}
                            size="small"
                        >
                            {translating ? 'Traduction...' : 'Traduire automatiquement'}
                        </Button>
                        {translationResult && (
                            <Chip 
                                label={`Vers: ${translationResult.targetLanguage}`}
                                size="small"
                                color="primary"
                                variant="outlined"
                            />
                        )}
                    </Box>

                    {/* Affichage de la traduction proposée */}
                    {showTranslation && translatedReason && (
                        <Box mb={2} p={2} sx={{ backgroundColor: '#e8f5e8', borderRadius: 1, border: '1px solid #4caf50' }}>
                            <Typography variant="subtitle2" color="success.main" gutterBottom>
                                Traduction proposée en {translationResult?.targetLanguage} :
                            </Typography>
                            <Typography variant="body2" sx={{ fontStyle: 'italic', mb: 2 }}>
                                "{translatedReason}"
                            </Typography>
                            <Alert severity="info" sx={{ fontSize: '0.875rem' }}>
                                Vérifiez la traduction avant de valider le rejet. Cette traduction sera envoyée au propriétaire.
                            </Alert>
                        </Box>
                    )}

                    {/* Notes de traduction en cas d'erreur */}
                    {translationResult && translationResult.error && (
                        <Box mb={2}>
                            <Alert severity="warning">
                                {translationResult.error === 'Quota épuisé' 
                                    ? 'Quota de traduction épuisé. Veuillez traduire manuellement le motif dans la langue du propriétaire.' 
                                    : 'Erreur de traduction. Veuillez vérifier que votre motif est en français et réessayer.'}
                            </Alert>
                        </Box>
                    )}
                    
                    <TextField
                        fullWidth
                        multiline
                        rows={2}
                        label="Notes internes (optionnel)"
                        value={validationNotes}
                        onChange={(e) => setValidationNotes(e.target.value)}
                        margin="normal"
                        placeholder="Notes pour l'équipe (non visibles par l'utilisateur)..."
                        disabled={submitting || translating}
                    />
                </DialogContent>
                <DialogActions>
                    <Button 
                        onClick={() => {
                            setRejectDialog({ open: false, listing: null });
                            setRejectReason('');
                            setTranslatedReason('');
                            setTranslationResult(null);
                            setShowTranslation(false);
                            setValidationNotes('');
                        }}
                        disabled={submitting || translating}
                    >
                        Annuler
                    </Button>
                    <Button
                        onClick={handleConfirmRejectionWithTranslation}
                        color="error"
                        variant="contained"
                        disabled={submitting || translating || !rejectReason.trim() || rejectReason.trim().length < 10}
                        startIcon={submitting ? <CircularProgress size={20} /> : <RejectIcon />}
                    >
                        {submitting ? 'Rejet...' : (showTranslation ? 'Confirmer le rejet avec traduction' : 'Rejeter')}
                    </Button>
                </DialogActions>
            </Dialog>
            
            {/* Dialog Détails */}
            <Dialog 
                open={detailDialog.open} 
                onClose={() => setDetailDialog({ open: false, listing: null })}
                maxWidth="md"
                fullWidth
            >
                <DialogTitle>Détails de l'annonce</DialogTitle>
                <DialogContent>
                    {detailDialog.listing && (
                        <Grid container spacing={3}>
                            <Grid item xs={12}>
                                <Typography variant="h6" gutterBottom>
                                    {detailDialog.listing.title}
                                </Typography>
                                {detailDialog.listing.price && (
                                    <Typography variant="h5" color="primary" gutterBottom>
                                        {detailDialog.listing.price}€
                                    </Typography>
                                )}
                            </Grid>
                            
                            {/* Images */}
                            {(detailDialog.listing.images || detailDialog.listing.Images) && 
                             (detailDialog.listing.images || detailDialog.listing.Images).length > 0 && (
                                <Grid item xs={12}>
                                    <Typography variant="subtitle2" gutterBottom>Images :</Typography>
                                    <ImageList cols={3} rowHeight={120} sx={{ maxHeight: 300 }}>
                                        {(detailDialog.listing.images || detailDialog.listing.Images).map((image, index) => (
                                            <ImageListItem key={index}>
                                                <img
                                                    src={image.image_url}
                                                    alt={`Image ${index + 1}`}
                                                    style={{ objectFit: 'cover', width: '100%', height: '100%' }}
                                                    loading="lazy"
                                                />
                                            </ImageListItem>
                                        ))}
                                    </ImageList>
                                </Grid>
                            )}
                            
                            {/* Description */}
                            {detailDialog.listing.description && (
                                <Grid item xs={12}>
                                    <Typography variant="subtitle2" gutterBottom>Description :</Typography>
                                    <Typography variant="body2" style={{ whiteSpace: 'pre-wrap' }}>
                                        {detailDialog.listing.description}
                                    </Typography>
                                </Grid>
                            )}
                            
                            {/* Informations */}
                            <Grid item xs={6}>
                                <Typography variant="subtitle2">Catégorie :</Typography>
                                <Typography variant="body2">{getCategoryName(detailDialog.listing)}</Typography>
                            </Grid>
                            <Grid item xs={6}>
                                <Typography variant="subtitle2">Ville :</Typography>
                                <Typography variant="body2">{detailDialog.listing.city || 'Non spécifiée'}</Typography>
                            </Grid>
                            <Grid item xs={6}>
                                <Typography variant="subtitle2">Propriétaire :</Typography>
                                <Typography variant="body2">
                                    {detailDialog.listing.owner?.name || detailDialog.listing.User?.name || 'Anonyme'}
                                </Typography>
                                <Typography variant="caption" color="textSecondary">
                                    {detailDialog.listing.owner?.email || detailDialog.listing.User?.email}
                                </Typography>
                            </Grid>
                            <Grid item xs={6}>
                                <Typography variant="subtitle2">Créée le :</Typography>
                                <Typography variant="body2">{formatDate(detailDialog.listing.createdAt)}</Typography>
                            </Grid>
                        </Grid>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDetailDialog({ open: false, listing: null })}>
                        Fermer
                    </Button>
                    <Button
                        color="success"
                        startIcon={<ValidateIcon />}
                        onClick={() => {
                            setDetailDialog({ open: false, listing: null });
                            setValidateDialog({ open: true, listing: detailDialog.listing });
                        }}
                    >
                        Valider
                    </Button>
                    <Button
                        color="error"
                        startIcon={<RejectIcon />}
                        onClick={() => {
                            setDetailDialog({ open: false, listing: null });
                            setRejectDialog({ open: true, listing: detailDialog.listing });
                        }}
                    >
                        Rejeter
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Dialog Action en lot */}
            <Dialog
                open={batchActionDialog.open}
                onClose={() => setBatchActionDialog({ open: false, action: null })}
                maxWidth="sm"
                fullWidth
            >
                <DialogTitle>Actions en lot</DialogTitle>
                <DialogContent>
                    <Typography variant="body1" gutterBottom>
                        Que souhaitez-vous faire avec les annonces sélectionnées ?
                    </Typography>
 {/* dangeruex : provoque boucle infinie
                    <Box display="flex" flexDirection="column" gap={2} mt={2}>
                        <Button
                            variant="contained"
                            color="success"
                            onClick={() => setBatchActionDialog({ open: true, action: 'validate' })}
                            disabled={selectedListings.length === 0 || submitting}
                            startIcon={submitting ? <CircularProgress size={20} /> : <ValidateIcon />}
                        >
                            {submitting ? 'Validation...' : 'Valider les annonces'}
                        </Button>
                        <Button
                            variant="contained"
                            color="error"
                            onClick={() => setBatchActionDialog({ open: true, action: 'reject' })}
                            disabled={selectedListings.length === 0 || submitting}
                            startIcon={submitting ? <CircularProgress size={20} /> : <RejectIcon />}
                        >
                            {submitting ? 'Rejet...' : 'Rejeter les annonces'}
                        </Button>
                    </Box>
*/}
                    {/* Motif de rejet en lot */}
                    {batchActionDialog.action === 'reject' && (
                        <>
                            <Typography variant="subtitle2" sx={{ mt: 2, mb: 1 }}>
                                Motif du rejet :
                            </Typography>
                            <TextField
                                fullWidth
                                multiline
                                rows={4}
                                label="Motif du rejet *"
                                value={batchRejectReason}
                                onChange={(e) => setBatchRejectReason(e.target.value)}
                                margin="normal"
                                required
                                error={batchRejectReason.trim().length > 0 && batchRejectReason.trim().length < 10}
                                helperText={
                                    batchRejectReason.trim().length > 0 && batchRejectReason.trim().length < 10
                                        ? 'Le motif doit contenir au moins 10 caractères'
                                        : 'Ce motif sera envoyé aux propriétaires des annonces'
                                }
                                placeholder="Expliquez précisément pourquoi ces annonces ne peuvent pas être validées..."
                                disabled={submitting}
                            />
                        </>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button
                        onClick={() => setBatchActionDialog({ open: false, action: null })}
                        disabled={submitting}
                    >
                        Annuler
                    </Button>
                    <Button
                        onClick={handleBatchAction}
                        color={batchActionDialog.action === 'validate' ? 'success' : 'error'}
                        variant="contained"
                        disabled={submitting || (batchActionDialog.action === 'reject' && (!batchRejectReason.trim() || batchRejectReason.trim().length < 10))}
                        startIcon={submitting ? <CircularProgress size={20} /> : (batchActionDialog.action === 'validate' ? <ValidateIcon /> : <RejectIcon />)}
                    >
                        {submitting ? (batchActionDialog.action === 'validate' ? 'Validation...' : 'Rejet...') : (batchActionDialog.action === 'validate' ? 'Valider' : 'Rejeter')}
                    </Button>
                </DialogActions>
            </Dialog>
              </Box>
            )}

            {/* Contenu onglet Produits */}
            {activeTab === 1 && (
              <Box>
                <ProductValidation />
              </Box>
            )}
        </Box>
    );
};

export default ListingValidation;
